import { Loader2, Send, Users } from 'lucide-react';

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@design-system';

import { ShareRecipientPicker } from './share-recipient-picker';
import { MAX_NOTE_LENGTH, useShareClientDialog } from './use-share-client-dialog';
import { LegacyShareAttestationDialog } from './legacy-share-attestation-dialog';

type Step = 'select' | 'note';

interface ShareClientWithSupervisorDialogProps {
  client: Client;
  partner?: Client | null;
  open: boolean;
  onClose: () => void;
}

export function ShareClientWithSupervisorDialog({
  client,
  partner,
  open,
  onClose,
}: ShareClientWithSupervisorDialogProps) {
  const dialog = useShareClientDialog<Step>({
    client,
    partner,
    open,
    onClose,
    initialStep: 'select',
    successToast: ({ recipientCount, clientCount }) => ({
      title: 'Client shared',
      description: `Shared ${clientCount} ${
        clientCount === 1 ? 'client' : 'clients'
      } with ${recipientCount} ${recipientCount === 1 ? 'supervisor' : 'supervisors'}.`,
    }),
    errorToastTitle: 'Failed to share client',
  });

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={isOpen => !isOpen && dialog.handleClose()}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-primary-dark">Share Client</DialogTitle>
            <DialogDescription className="text-foreground">
              {dialog.step === 'select'
                ? 'Choose which supervisor(s) to share this client with'
                : 'Add an optional note'}
            </DialogDescription>
          </DialogHeader>

          {dialog.step === 'select' && (
            <>
              <div className="py-2">
                <div className="mb-4 flex items-start gap-3">
                  <div className="bg-primary/5 flex size-12 shrink-0 items-center justify-center rounded-full">
                    <Users className="text-primary-dark h-6 w-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="mb-0.5 text-base font-semibold text-gray-950">
                      Share <span className="text-primary-dark">{dialog.clientDisplayName}</span>{' '}
                      with
                    </h3>
                    <p className="text-foreground text-sm">
                      Selected supervisors will be able to view this client.
                    </p>
                  </div>
                </div>

                {dialog.hasPartner && dialog.partnerDisplayName && (
                  <label
                    htmlFor="share-include-partner"
                    className="bg-muted/50 hover:bg-muted mb-3 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors"
                  >
                    <Checkbox
                      id="share-include-partner"
                      checked={dialog.includePartner}
                      onCheckedChange={checked => dialog.setIncludePartner(checked === true)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-950">
                        Also share their partner ({dialog.partnerDisplayName})
                      </p>
                      <p className="text-foreground text-xs">
                        The same supervisors will get access to the partner record with the same
                        note.
                      </p>
                    </div>
                  </label>
                )}

                <ShareRecipientPicker
                  recipients={dialog.filteredRecipients}
                  selectedIds={dialog.selectedIds}
                  alreadySharedIds={dialog.alreadySharedIds}
                  onToggle={dialog.toggleRecipient}
                  search={dialog.search}
                  onSearchChange={dialog.setSearch}
                  isLoading={dialog.isLoadingRecipients}
                  hasNoRecipients={dialog.hasNoRecipients}
                  loadingLabel="Loading supervisors…"
                  noRecipientsLabel="You're not on any team yet. To share clients, you need to be a member of an organization first."
                  searchPlaceholder="Search by name, email, or organization…"
                  noMatchesLabel="No supervisors match"
                />

                {dialog.selectedIds.length > 0 && (
                  <p className="text-foreground mt-2 text-xs">
                    {dialog.selectedIds.length} selected
                  </p>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={dialog.handleClose}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => dialog.setStep('note')}
                  disabled={dialog.selectedIds.length === 0}
                >
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}

          {dialog.step === 'note' && (
            <>
              <div className="py-2">
                <div className="mb-4">
                  <h3 className="mb-1 text-base font-semibold text-gray-950">
                    Add a note (optional)
                  </h3>
                  <p className="text-foreground text-sm">
                    Share context with the recipient, for example, "Please review the quotes I
                    pulled."
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label
                    htmlFor="share-note"
                    className="text-sm"
                  >
                    Note
                  </Label>
                  <textarea
                    id="share-note"
                    rows={5}
                    maxLength={MAX_NOTE_LENGTH}
                    value={dialog.note}
                    onChange={e => dialog.setNote(e.target.value)}
                    placeholder="Optional note…"
                    className="bg-background placeholder:text-muted-foreground focus:ring-primary flex w-full rounded-md border px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2"
                  />
                  <p className="text-muted-foreground text-xs">
                    {dialog.note.length} / {MAX_NOTE_LENGTH}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => dialog.setStep('select')}
                  disabled={dialog.isPending}
                >
                  Back
                </Button>
                <Button
                  onClick={() =>
                    dialog.needsLegacyAttestation ? dialog.setAttestationOpen(true) : dialog.share()
                  }
                  disabled={dialog.isPending}
                >
                  {dialog.isPending ? (
                    <>
                      <Loader2 className="mr-1 size-4 animate-spin" />
                      Sharing…
                    </>
                  ) : (
                    <>
                      <Send className="mr-1 size-4" />
                      Share
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <LegacyShareAttestationDialog
        clientId={client.clientId}
        open={dialog.attestationOpen}
        onConfirmed={() => {
          dialog.setAttestationOpen(false);
          dialog.share();
        }}
        onCancel={() => dialog.setAttestationOpen(false)}
      />
    </>
  );
}
